import { nanoid } from 'nanoid'

export function assertDefined<T>(value: T, info?: string): asserts value {
    if (value == null) {
        throw Error(joinLines(
            `Expected a value to be defined, got undefined`,
            (info != null) ? `| Info: ${info}` : ''
        ))
    }
}

/**
 * Expects a boolean expression that will evaluate to true. Otherwise
 * an error with the given message will be thrown.
 */
export function assertInvariant(invariant: boolean, ...errorMessage: string[]) {
    if (invariant === false) {
        const message = Array.isArray(errorMessage)
            ? errorMessage.join(` `)
            : errorMessage

        throw Error(joinLines(
            `Invariant violation! -`,
            message
        ))
    }
}

export function joinLines(...parts: string[]): string {
    return parts.join(" ")
}

export function extractOneFromList<T>(list: T[], predicate: (item: T) => boolean): { match: T; restOfList: T[] } {
    return list.reduce<{ match: T; restOfList: T[] }>((acc, curr) => {

        if (predicate(curr)) {
            if (acc.match != null) {
                throw Error(`Expected exactly one match, found several: [${list}]`)
            }

            return {
                ...acc,
                match: curr,
            }
        } else {
            acc.restOfList.push(curr)
            return acc
        }

    }, {
        match: undefined as unknown as T,
        restOfList: []
    })
}

export function jsonClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj))
}

export function updateListItem<T extends { id: string }>(
    list: T[],
    id: string,
    props: Partial<T>
) {
    const itemIndex = list.findIndex(i => i.id === id)

    if (itemIndex < 0) {
        throw Error(`Can only update items present in the list`)
    }

    const item = list[itemIndex]
    const updatedList = [...list]

    updatedList.splice(itemIndex, 1, {
        ...item,
        ...props
    })

    return updatedList
}

export function removeListItem<T extends { id: string }>(list: T[], id: string) {
    return list.filter(item => item.id !== id)
}

export function expectOneById<T extends { id: string }>(list: T[], id: string): T {
    if (id == null) {
        throw Error(`Illegal param, id cannot be undefined`)
    }

    const item = list.filter(item => item.id === id)

    if (item.length !== 1) {
        throw Error([
            `Expected a single item on id ${id}`,
            `found ${item.length}`
        ].join(' '))
    }

    return item[0]
}

export function mustBeDefined<T>(value: T | undefined, defaultValue: T): T {
    if (value == null) {
        return defaultValue
    } else {
        return value
    }
}

export function emptyStringAsUndefined(value: string | number | undefined): string | number | undefined {
    if (value == null) {
        return undefined
    }

    if (value === '') {
        return undefined
    }

    return value
}

export function numberOrUndefined(value: string | number | undefined): number | undefined {
    if (value == null) {
        return undefined
    }

    if (value === '') {
        return undefined
    }

    if (typeof value === 'number') {
        return value
    }

    return parseInt(value)
}

export function idMapper<T extends { id: string }>() {

    const idMap: Map<string, string> = new Map()

    return {
        /**
         * Creates a new ID on the given entity, mutating it.
         */
        newId: (entity: T): void => {
            const newId = nanoid(10)
            idMap.set(entity.id, newId)
            entity.id = newId
        },
        /**
         * Returns the new ID for the given old ID, used by
         * referencing entities to resolve their new relationship.
         */
        swap: (oldId: string): string => {
            const newId = idMap.get(oldId)

            if (newId == null) {
                throw Error(`Failed to find new id for ${oldId}`)
            }

            return newId
        }
    }
}

export function nextOrdinal<T extends { ordinal: number }>(list: T[]): number {
    if (list.length <= 0) {
        return 0
    }

    const highestOrdinalFirst = [...list].sort((a, b) => b.ordinal - a.ordinal)
    return highestOrdinalFirst[0].ordinal + 1
}

export function getListFor<T>(list: T[], predicate: (item: T) => boolean): T[] {
    return list.filter(predicate)
}

export function sortByOrdinal<T extends { ordinal: number }>(list: T[]): T[] {
    return [...list].sort((a, b) => a.ordinal - b.ordinal)
}

export function splitList<T>(list: T[], predicate: (item: T) => boolean): { taken: T[]; remaining: T[] } {
    const piles: { taken: T[]; remaining: T[] } = {
        taken: [],
        remaining: [],
    }

    list.forEach(item => {
        if (predicate(item)) {
            piles.taken.push(item)
        } else {
            piles.remaining.push(item)
        }
    })

    return piles
}